/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React, {Fragment} from 'react'
import {useIntl} from 'react-intl'
import {
    Flex,
    //Button,
    useMultiStyleConfig,
    VStack,
    Text,
    Image,
} from '@salesforce/retail-react-app/app/components/shared/ui'
// import {LockIcon} from '@salesforce/retail-react-app/app/components/icons'
import {useCurrentBasket} from '@salesforce/retail-react-app/app/hooks/use-current-basket'
import {useCustomPreferenceWithLocale} from '../../../hooks/use-custom-preference'

//ICONS
import {
    AmericanexpressIcon,
    ApplepayIcon,
    KlarnaIcon,
    MastercardIcon,
    PaypalIcon,
    TwintIcon,
    VisaIcon,
} from '../../../components/icons'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'

const paymentIcons = [
    {
        key: 'applepay',
        icon: ApplepayIcon,
        label: {
            id: 'payment_methods.applepay.label',
            defaultMessage: 'Apple Pay payment method',
        },
    },
    {
        key: 'amex',
        icon: AmericanexpressIcon,
        label: {
            id: 'payment_methods.amex.label',
            defaultMessage: 'American Express payment method',
        },
    },
    {
        key: 'mastercard',
        icon: MastercardIcon,
        label: {
            id: 'payment_methods.mastercard.label',
            defaultMessage: 'Mastercard payment method',
        },
    },
    {
        key: 'paypal',
        icon: PaypalIcon,
        label: {
            id: 'payment_methods.paypal.label',
            defaultMessage: 'PayPal payment method',
        },
    },
    {
        key: 'klarna',
        icon: KlarnaIcon,
        label: {
            id: 'payment_methods.klarna.label',
            defaultMessage: 'Klarna payment method',
        },
    },
    {
        key: 'visa',
        icon: VisaIcon,
        label: {
            id: 'payment_methods.visa.label',
            defaultMessage: 'Visa payment method',
        },
    },
    {
        key: 'twint',
        icon: TwintIcon,
        label: {
            id: 'payment_methods.twint.label',
            defaultMessage: 'Twint payment method',
        },
    },
]

const subscriptionPaymentKeys = ['mastercard', 'visa', 'amex']

const CartCta = () => {
    const style = useMultiStyleConfig('Cart', {})
    const {formatMessage} = useIntl()
    const {paymentMethods} = style
    const {data: basket} = useCurrentBasket()
    const {locale} = useMultiSite()

    const paymentLinksForLocale = useCustomPreferenceWithLocale('paymentLogosOrderSummary')

    const hasSubscription = basket?.productItems?.some((item) => item?.c_isSubscription)

    let iconsToShow

    if (hasSubscription) {
        // If there is a subscription, always use the subscription payment keys.
        iconsToShow = paymentIcons.filter((p) => subscriptionPaymentKeys.includes(p.key))
    } else if (paymentLinksForLocale) {
        // If no subscription and we have links from the custom preference, use them.
        iconsToShow = Object.entries(paymentLinksForLocale).map(([key, src]) => ({
            key,
            src,
        }))
    } else {
        // Otherwise, use the fallback logic with local icons (excluding subscription check).
        iconsToShow = paymentIcons.filter((p) => {
            if (p.key === 'twint') {
                return locale.alias === 'fr-ch' || locale.alias === 'de-ch'
            }
            return true
        })
    }

    return (
        <Fragment>
            {/* <Button
                as={Link}
                to="/checkout"
                width={['95%', '95%', '95%', '100%']}
                marginTop={[6, 6, 2, 2]}
                mb={4}
                rightIcon={<LockIcon />}
                variant="solid"
            >
                <FormattedMessage
                    defaultMessage="Proceed to Checkout"
                    id="cart_cta.link.checkout"
                />
            </Button> */}
            <VStack {...paymentMethods?.stack}>
                <Flex {...paymentMethods?.headerContainer}>
                    <Text {...paymentMethods?.header}>
                        {formatMessage({
                            id: 'cart_default.cart_cta.header',
                            defaultMessage: 'Payment Methods available',
                        })}
                    </Text>
                </Flex>
                <Flex {...paymentMethods?.iconContainer}>
                    {/* Conditionally render based on whether we have custom links or are using the fallback */}
                    {paymentLinksForLocale && !hasSubscription
                        ? iconsToShow.map(({key, src}) => (
                              <Image key={key} src={src} alt={key} {...paymentMethods?.icon} />
                          ))
                        : iconsToShow.map(({key, icon: Icon, label}) => (
                              <Icon
                                  key={key}
                                  {...paymentMethods?.icon}
                                  aria-label={formatMessage(label)}
                              />
                          ))}
                    {/*TODO : MISSING AFFIRM AND AFTER PAY */}
                </Flex>
            </VStack>
        </Fragment>
    )
}

export default CartCta
